import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Typography,
} from '@mui/material';
import { getRawTimes, submitRawTimeCandidates } from './api';
import { Link } from 'react-router-dom';

export default function RawTimes() {
  const [rawTimes, setRawTimes] = useState([]);
  const [candidates, setCandidates] = useState([]);
  const [event, setEvent] = useState({});
  const [selectedCandidates, setSelectedCandidates] = useState({});
  const [status, setStatus] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [bibFilter, setBibFilter] = useState('');
  const [unmappedSystems, setUnmappedSystems] = useState([]);
  const [mappingTableReady, setMappingTableReady] = useState(true);
  const mounted = useRef(true);

  const loadRawTimes = () => {
    getRawTimes()
      .then((data) => {
        if (!mounted.current || !data) return;

        const nextRawTimes = Array.isArray(data.raw_times) ? data.raw_times : [];
        const nextCandidates = Array.isArray(data.candidates) ? data.candidates : [];
        setRawTimes(nextRawTimes);
        setCandidates(nextCandidates);
        setEvent(data.event || {});
        setUnmappedSystems(Array.isArray(data.unmapped_systems) ? data.unmapped_systems : []);
        setMappingTableReady(data.mapping_table_ready !== false);
        setSelectedCandidates((current) => {
          const next = {};
          nextCandidates.forEach((row) => {
            const candidateId = String(row.candidate_id);
            next[candidateId] = Object.prototype.hasOwnProperty.call(current, candidateId)
              ? current[candidateId]
              : true;
          });
          return next;
        });
      })
      .catch(() => {
        if (mounted.current) setStatus('Unable to load raw times');
      });
  };

  useEffect(() => {
    mounted.current = true;
    loadRawTimes();
    const interval = setInterval(loadRawTimes, 10000);

    return () => {
      mounted.current = false;
      clearInterval(interval);
    };
  }, []);

  const filteredRawTimes = useMemo(() => {
    const filter = bibFilter.trim();
    if (filter.length === 0) return rawTimes;
    return rawTimes.filter((row) => String(row.bib || '') === filter);
  }, [rawTimes, bibFilter]);

  const selectedCount = useMemo(() => {
    return candidates.filter((row) => selectedCandidates[String(row.candidate_id)]).length;
  }, [candidates, selectedCandidates]);

  const setCandidateSelected = (candidateId, checked) => {
    setSelectedCandidates((current) => ({ ...current, [String(candidateId)]: checked }));
  };

  const submitSelected = () => {
    const candidateIds = candidates
      .filter((row) => selectedCandidates[String(row.candidate_id)])
      .map((row) => row.candidate_id);

    if (candidateIds.length === 0) {
      setStatus('No graded entries selected');
      return;
    }

    setSubmitting(true);
    setStatus('Submitting graded finish times');
    submitRawTimeCandidates(candidateIds)
      .then((data) => {
        const inserted = data && data.inserted ? data.inserted : 0;
        setStatus(`Submitted ${inserted} finish time${inserted === 1 ? '' : 's'}`);
        loadRawTimes();
      })
      .catch(() => {
        setStatus('Unable to submit graded finish times');
      })
      .finally(() => {
        setSubmitting(false);
      });
  };

  return (
    <div className="raw-times-page">
      <div className="raw-times-header">
        <div>
          <h2>Raw Times</h2>
          <Typography variant="caption">
            Event {event && event.event_id ? event.event_id : ''} {event && event.name ? `- ${event.name}` : ''}
          </Typography>
        </div>
        <div className="raw-times-actions">
          <input
            className="raw-times-filter"
            type="text"
            value={bibFilter}
            placeholder="Bib"
            onChange={(e) => setBibFilter(e.target.value)}
          />
          <Button variant="outlined" onClick={loadRawTimes} disabled={submitting}>
            Refresh
          </Button>
          <Button variant="contained" onClick={submitSelected} disabled={submitting || selectedCount === 0}>
            Submit {selectedCount}
          </Button>
        </div>
      </div>

      {status && <div className="raw-times-status">{status}</div>}
      {unmappedSystems.length > 0 && (
        <div className="raw-times-status">
          Readers with raw times but no split mapping: {unmappedSystems.join(', ')}.{' '}
          <Link to="/raw-time-mappings">Map readers</Link>
          {!mappingTableReady ? ' after raw_time_system_splits is created.' : '.'}
        </div>
      )}

      <div className="raw-times-grid">
        <section className="raw-times-panel">
          <div className="raw-times-panel-header">
            <h3>Raw Feed</h3>
            <span>{filteredRawTimes.length} shown</span>
          </div>
          <TableContainer className="raw-times-scroll">
            <Table size="small">
              <TableBody>
                <TableRow>
                  <TableCell>Timestamp</TableCell>
                  <TableCell>System</TableCell>
                  <TableCell>Bib</TableCell>
                  <TableCell>Time</TableCell>
                </TableRow>
                {filteredRawTimes.map((row, index) => (
                  <TableRow key={`${row.timestamp}-${row.system}-${row.bib}-${index}`}>
                    <TableCell>{row.timestamp}</TableCell>
                    <TableCell>{row.system}</TableCell>
                    <TableCell>
                      <Link to={`/athlete?${row.bib}`}>
                        <span>{row.bib}</span>
                      </Link>
                    </TableCell>
                    <TableCell>{row.time}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </section>

        <section className="raw-times-panel">
          <div className="raw-times-panel-header">
            <h3>Graded Finish Candidates</h3>
            <span>{candidates.length} missing finishes</span>
          </div>
          <TableContainer className="raw-times-scroll">
            <Table size="small">
              <TableBody>
                <TableRow>
                  <TableCell>Submit</TableCell>
                  <TableCell>Bib</TableCell>
                  <TableCell>Split</TableCell>
                  <TableCell>Median Time</TableCell>
                  <TableCell>Raw</TableCell>
                  <TableCell>Window</TableCell>
                  <TableCell>Systems</TableCell>
                  <TableCell>Last Seen</TableCell>
                </TableRow>
                {candidates.map((row) => (
                  <TableRow key={row.candidate_id}>
                    <TableCell>
                      <input
                        type="checkbox"
                        checked={Boolean(selectedCandidates[String(row.candidate_id)])}
                        onChange={(e) => setCandidateSelected(row.candidate_id, e.target.checked)}
                      />
                    </TableCell>
                    <TableCell>
                      <Link to={`/athlete?${row.bib}`}>
                        <span>{row.bib}</span>
                      </Link>
                    </TableCell>
                    <TableCell>{row.splits}</TableCell>
                    <TableCell>{row.clock_time}</TableCell>
                    <TableCell>{row.raw_count}</TableCell>
                    <TableCell>{row.window_seconds}s</TableCell>
                    <TableCell>{row.systems}</TableCell>
                    <TableCell>{row.last_timestamp}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </section>
      </div>
    </div>
  );
}
